import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { compressImage, saveLocalPhoto } from "@/lib/offline";
import { PhotoImg } from "@/components/PhotoImg";
import { Button } from "@/components/ui/button";
import { Camera, Loader2, Trash2, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const MAX_PHOTOS = 6;

export function PhotoUploader({ photos, onChange }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const room = MAX_PHOTOS - (photos || []).length;
    if (room <= 0) { toast.error(`Maximum ${MAX_PHOTOS} photos par bon`); return; }
    const list = Array.from(files).slice(0, room);
    if (files.length > room) toast.warning(`Seules ${room} photo(s) ajoutée(s) : maximum ${MAX_PHOTOS} par bon`);
    setUploading(true);
    try {
      const uploaded = [];
      let kept = 0;
      for (const file of list) {
        const blob = await compressImage(file).catch(() => file);
        let ph = null;
        if (navigator.onLine) {
          try {
            const fd = new FormData();
            fd.append("file", blob, "photo.jpg");
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            ph = { storage_path: data.storage_path, caption: "", pk: "" };
          } catch (e) { if (e.response) throw e; }
        }
        if (!ph) { ph = { local_id: await saveLocalPhoto(blob), caption: "", pk: "" }; kept++; }
        uploaded.push(ph);
      }
      onChange([...(photos || []), ...uploaded]);
      if (kept) toast.success(`${kept} photo(s) gardée(s) sur l'appareil, envoi au retour du réseau`);
      else toast.success(`${uploaded.length} photo(s) ajoutée(s)`);
    } catch (e) {
      toast.error("Échec du téléversement");
    } finally {
      setUploading(false);
    }
  };

  const updatePhoto = (idx, field, val) => {
    const next = [...photos];
    next[idx] = { ...next[idx], [field]: val };
    onChange(next);
  };
  const removePhoto = (idx) => onChange(photos.filter((_, i) => i !== idx));

  const full = (photos || []).length >= MAX_PHOTOS;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {/* Camera: opens the device camera directly */}
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden
          data-testid="photo-camera-input" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
        {/* Gallery: pick existing images (multiple) */}
        <input ref={galleryRef} type="file" accept="image/*" multiple hidden
          data-testid="photo-gallery-input" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />

        <Button type="button" onClick={() => cameraRef.current?.click()} disabled={uploading || full}
          data-testid="photo-camera-btn"
          className="h-11 gap-2 bg-amber-500 font-semibold text-slate-900 hover:bg-amber-400">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
          Prendre une photo
        </Button>
        <Button type="button" variant="outline" onClick={() => galleryRef.current?.click()} disabled={uploading || full}
          data-testid="photo-gallery-btn"
          className="h-11 gap-2">
          <ImagePlus className="h-4 w-4" /> Galerie
        </Button>
        <span className={`text-xs font-semibold ${full ? "text-amber-600" : "text-slate-400"}`} data-testid="photo-count">
          {(photos || []).length} / {MAX_PHOTOS} photos
        </span>
      </div>

      {(photos || []).length === 0 ? (
        <div className="mt-3 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 py-10 text-slate-400 dark:border-slate-700">
          <ImagePlus className="h-8 w-8" />
          <p className="mt-2 text-sm">Aucune photo. Ajoutez des clichés du chantier.</p>
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {photos.map((ph, idx) => (
            <div key={idx} data-testid={`photo-item-${idx}`}
              className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
              <div className="relative aspect-video bg-slate-100 dark:bg-slate-800">
                <PhotoImg photo={ph} className="h-full w-full cursor-pointer object-cover" onOpen={setPreview} testId={`photo-img-${idx}`} />
                {ph.local_id && !ph.storage_path && (
                  <span className="absolute bottom-1.5 left-1.5 rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-slate-900" data-testid={`photo-local-${idx}`}>Sur l'appareil</span>
                )}
                <button type="button" onClick={() => removePhoto(idx)} data-testid={`photo-remove-${idx}`}
                  className="absolute right-1.5 top-1.5 rounded-full bg-red-600/90 p-1.5 text-white hover:bg-red-700">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="space-y-1.5 p-2">
                <input value={ph.pk || ""} onChange={(e) => updatePhoto(idx, "pk", e.target.value)}
                  placeholder="PK / Localisation" data-testid={`photo-pk-${idx}`}
                  className="blue-field h-8 w-full rounded px-2 text-xs" />
                <input value={ph.caption || ""} onChange={(e) => updatePhoto(idx, "caption", e.target.value)}
                  placeholder="Légende" data-testid={`photo-caption-${idx}`}
                  className="blue-field h-8 w-full rounded px-2 text-xs" />
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={() => setPreview(null)}>
          <img src={preview} alt="aperçu" className="max-h-full max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
}
